import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, User, ChevronDown, ArrowUpRight, Search, Box } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useCategories } from "@/context/CategoriesContext";
import { categoryName, categoryDesc } from "@/lib/category";
import { api, img, formatPrice } from "@/lib/api";
import { productName } from "@/lib/product";
import { HeaderLangSwitch } from "@/components/HeaderLangSwitch";
import { LANGUAGES } from "@/i18n";
import { switchLanguage } from "@/lib/lang";

const links = [
  { key: "guides", to: "/blog" },
  { key: "about", to: "/about" },
  { key: "yesWeKann", to: "/yes-we-kann" },
];

// nav items participating in the sliding active/hover indicator
const navItems = [{ key: "shop", to: "/shop" }, ...links];

function activeNavKey(pathname) {
  if (pathname.startsWith("/shop")) return "shop";
  if (pathname.startsWith("/configurator")) return "configurator";
  if (pathname.startsWith("/blog")) return "guides";
  if (pathname.startsWith("/about")) return "about";
  if (pathname.startsWith("/yes-we-kann")) return "yesWeKann";
  return null;
}

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { count, setOpen } = useCart();
  const { user, isAdmin } = useAuth();
  const { settings } = useSettings();
  const { hasNew } = useNotifications();
  const { categories: cats } = useCategories();
  const navigate = useNavigate();
  const menu = (cats || []).map((c) => ({
    id: c.id, slug: c.slug, to: `/shop/${c.slug}`,
    label: categoryName(c, i18n.language), desc: categoryDesc(c, i18n.language), image: c.image,
  }));
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [sugLoading, setSugLoading] = useState(false);
  const searchInputRef = useRef(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  const activeKey = activeNavKey(location.pathname);
  const indicated = hovered ?? activeKey;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setShopOpen(false);
    setSearchOpen(false);
  }, [location]);

  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 60);
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) { setSuggestions([]); setSugLoading(false); return; }
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); setSugLoading(false); return; }
    setSugLoading(true);
    let active = true;
    const timer = setTimeout(() => {
      api
        .get("/products", { params: { q } })
        .then((r) => { if (active) setSuggestions((r.data || []).slice(0, 5)); })
        .catch(() => { if (active) setSuggestions([]); })
        .finally(() => { if (active) setSugLoading(false); });
    }, 220);
    return () => { active = false; clearTimeout(timer); };
  }, [query, searchOpen]);

  const goToProduct = (p) => {
    navigate(`/product/${p.slug || p.id}`);
    setSearchOpen(false);
    setQuery("");
    setSuggestions([]);
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) { searchInputRef.current?.focus(); return; }
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setQuery("");
  };

  const openShop = () => {
    clearTimeout(closeTimer.current);
    setShopOpen(true);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setShopOpen(false), 140);
  };

  const dark = scrolled || shopOpen;

  return (
    <header
      data-testid="site-header"
      style={{ top: "var(--season-banner-h, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-all duration-300 ${
        dark ? "bg-white/80 backdrop-blur-xl" : "bg-transparent"
      } ${scrolled ? "shadow-[0_4px_24px_-12px_rgba(0,0,0,0.15)]" : ""}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <motion.div whileHover={{ scale: 1.12, y: -2 }} whileTap={{ scale: 0.95 }} transition={{ type: "spring", stiffness: 250, damping: 26 }} className="origin-left">
          <Link to="/" data-testid="logo-link" className="font-logo text-xl md:text-2xl text-zinc-900 leading-none tracking-tight">
            {settings.brand.logo ? (
              <img src={settings.brand.logo} alt={settings.brand.name} className="h-7 md:h-8 w-auto object-contain" />
            ) : (
              <>{settings.brand.name}<span className="text-gold">.</span></>
            )}
          </Link>
        </motion.div>

        <nav className="hidden md:flex items-center gap-8" onMouseLeave={() => setHovered(null)}>
          {/* Shop mega-menu trigger */}
          <div className="relative" onMouseEnter={() => { setHovered("shop"); openShop(); }} onMouseLeave={scheduleClose}>
            <Link
              to="/shop"
              data-testid="nav-shop"
              className={`group relative inline-flex items-center gap-1 text-sm transition-colors duration-200 ${
                indicated === "shop" ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t("nav.shop")}
              <ChevronDown
                strokeWidth={1.5}
                className={`w-3.5 h-3.5 transition-transform duration-300 ${shopOpen ? "rotate-180" : ""}`}
              />
              {indicated === "shop" && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-1.5 left-0 right-0 h-0.5 rounded-full bg-gold"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
            </Link>
          </div>

          {links.map((n) => (
            <Link
              key={n.key}
              to={n.to}
              data-testid={`nav-${n.key.toLowerCase()}`}
              onMouseEnter={() => setHovered(n.key)}
              className={`group relative text-sm transition-colors duration-200 ${
                indicated === n.key ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
              }`}
            >
              {t(`nav.${n.key}`)}
              {indicated === n.key && (
                <motion.span
                  layoutId="nav-underline"
                  className="absolute -bottom-1.5 left-0 right-0 h-0.5 rounded-full bg-gold"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2">
          {/* Search (c) — desktop */}
          <div className="hidden md:flex items-center relative">
            <AnimatePresence initial={false}>
              {searchOpen && (
                <motion.form
                  key="header-search"
                  onSubmit={submitSearch}
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "14rem", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 30, mass: 0.9 }}
                  className="overflow-hidden"
                  style={{ transformOrigin: "right center" }}
                >
                  <motion.input
                    ref={searchInputRef}
                    initial={{ x: 24, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 24, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
                    placeholder={t("nav.searchPlaceholder", "Zoek producten…")}
                    data-testid="header-search-input"
                    className="w-full bg-zinc-100 focus:bg-white rounded-full pl-4 pr-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 border border-zinc-200 focus:border-zinc-300 outline-none focus:outline-none focus:ring-0 transition-colors"
                  />
                </motion.form>
              )}
            </AnimatePresence>
            <motion.button
              whileHover={{ scale: 1.15, rotate: -12 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 400, damping: 12 }}
              onClick={() => {
                if (!searchOpen) setSearchOpen(true);
                else if (query.trim()) submitSearch();
                else setSearchOpen(false);
              }}
              data-testid="header-search-toggle"
              className="group p-2.5 rounded-full"
              aria-label={searchOpen ? "Search" : "Open search"}
            >
              {searchOpen && !query.trim()
                ? <X strokeWidth={1.5} className="w-5 h-5 text-zinc-900 transition-colors duration-200 group-hover:text-gold" />
                : <Search strokeWidth={1.5} className="w-5 h-5 text-zinc-900 transition-colors duration-200 group-hover:text-gold" />}
            </motion.button>

            {/* Live search suggestions */}
            <AnimatePresence>
              {searchOpen && query.trim().length >= 2 && (
                <motion.div
                  key="search-suggestions"
                  initial={{ opacity: 0, y: -6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="search-suggestions"
                  className="absolute top-full right-0 mt-3 w-[22rem] rounded-2xl bg-white/95 backdrop-blur-xl border border-zinc-200/70 shadow-[0_16px_48px_-16px_rgba(0,0,0,0.25)] overflow-hidden z-50"
                >
                  {sugLoading && suggestions.length === 0 ? (
                    <div className="px-4 py-6 flex items-center gap-3 text-sm text-zinc-500" data-testid="search-loading">
                      <span className="w-4 h-4 rounded-full border-2 border-zinc-300 border-t-gold animate-spin" />
                      {t("nav.searching", "Zoeken…")}
                    </div>
                  ) : suggestions.length > 0 ? (
                    <>
                      <ul className="py-2">
                        {suggestions.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onClick={() => goToProduct(p)}
                              data-testid={`search-suggestion-${p.id}`}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-100/80 transition-colors group/item"
                            >
                              <span className="w-11 h-11 rounded-lg bg-zinc-100 overflow-hidden shrink-0">
                                {p.images?.[0] && (
                                  <img src={img(p.images[0], 120)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                )}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-zinc-900 group-hover/item:text-gold transition-colors">
                                  {productName(p, i18n.language)}
                                </span>
                                <span className="block text-xs text-zinc-500">{formatPrice(p.price)}</span>
                              </span>
                              <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 text-zinc-300 group-hover/item:text-gold transition-colors shrink-0" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <button
                        type="button"
                        onClick={() => submitSearch()}
                        data-testid="search-see-all"
                        className="w-full px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-zinc-600 hover:text-gold border-t border-zinc-100 transition-colors"
                      >
                        {t("nav.seeAllResults", "Alle resultaten bekijken")}
                      </button>
                    </>
                  ) : (
                    <div className="px-4 py-6 text-sm text-zinc-500" data-testid="search-no-results">
                      {t("nav.noResults", "Geen producten gevonden")}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="hidden md:block">
            <HeaderLangSwitch />
          </div>

          <motion.div whileHover={{ scale: 1.12, y: -2 }} whileTap={{ scale: 0.95 }} transition={{ type: "spring", stiffness: 250, damping: 26 }} className="relative">
            <Link
              to={user ? "/account" : "/login"}
              data-testid="account-link"
              className="group relative block p-2.5 rounded-full"
              aria-label="Account"
            >
              <User strokeWidth={1.5} className="w-5 h-5 text-zinc-900 transition-colors duration-200 group-hover:text-gold" />
              {isAdmin && hasNew && (
                <span data-testid="admin-notif-glow" className="pointer-events-none absolute inset-0">
                  <span className="absolute inset-0 rounded-full ring-2 ring-gold animate-ping opacity-75" />
                  <span className="absolute inset-0 rounded-full ring-2 ring-gold shadow-[0_0_12px_3px_rgba(219,150,13,0.55)] animate-pulse" />
                </span>
              )}
            </Link>
          </motion.div>
          <motion.button
            whileHover={{ scale: 1.12, y: -2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 250, damping: 26 }}
            data-testid="open-cart-button"
            onClick={() => setOpen(true)}
            className="group relative p-2.5 rounded-full"
            aria-label="Open cart"
          >
            <ShoppingBag strokeWidth={1.5} className="w-5 h-5 text-zinc-900 transition-colors duration-200 group-hover:text-gold" />
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 20 }}
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-zinc-900 text-white text-[10px] font-semibold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full"
              >
                {count}
              </motion.span>
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.9 }}
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden p-2.5 hover:bg-zinc-100 rounded-full transition-colors"
            aria-label="Menu"
          >
            {menuOpen ? <X strokeWidth={1.5} className="w-5 h-5" /> : <Menu strokeWidth={1.5} className="w-5 h-5" />}
          </motion.button>
        </div>
      </div>

      {/* Desktop mega-menu */}
      <AnimatePresence>
        {shopOpen && (
          <motion.div
            key="mega"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            onMouseEnter={openShop}
            onMouseLeave={scheduleClose}
            data-testid="shop-mega-menu"
            className="hidden md:block absolute inset-x-0 top-full bg-white/90 backdrop-blur-xl border-b border-zinc-200/60"
          >
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-8">
              <div className="flex items-center justify-between mb-5">
                <span className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">{t("nav.shopByDevice")}</span>
                <Link to="/shop" data-testid="mega-shop-all" className="group inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900">
                  {t("nav.shopAll")}
                  <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {menu.map((c, i) => (
                  <motion.div
                    key={c.id || c.slug}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Link
                      to={c.to}
                      data-testid={`mega-cat-${c.slug}`}
                      className="group flex items-center gap-4 rounded-2xl border border-zinc-200/70 bg-white/60 p-4 hover:border-gold hover:shadow-[0_8px_30px_-12px_rgba(219,150,13,0.4)] transition-all duration-300"
                    >
                      {c.image && (
                        <img src={img(c.image, 120)} alt="" loading="lazy" className="w-14 h-14 rounded-xl object-cover bg-zinc-100 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-display text-base font-medium text-zinc-900 truncate">{c.label}</span>
                          <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 shrink-0 text-zinc-300 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                        </div>
                        {c.desc && <p className="mt-1 text-sm text-zinc-500 line-clamp-2">{c.desc}</p>}
                      </div>
                    </Link>
                  </motion.div>
                ))}

                {/* Custom 3D-print — same tile size as categories, only a subtle gold icon accent */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + menu.length * 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to="/configurator"
                    data-testid="mega-configurator"
                    className="group flex items-center gap-4 rounded-2xl border border-gold/30 bg-gold/[0.07] p-4 hover:border-gold hover:bg-gold/[0.12] hover:shadow-[0_8px_30px_-12px_rgba(219,150,13,0.4)] transition-all duration-300"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-display text-base font-medium text-zinc-900 truncate flex items-center gap-2">
                          <Box strokeWidth={1.75} className="w-4 h-4 shrink-0 text-gold" /> {t("nav.configurator")}
                        </span>
                        <ArrowUpRight strokeWidth={1.5} className="w-4 h-4 shrink-0 text-gold/50 group-hover:text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </div>
                      <p className="mt-1 text-sm text-zinc-500 line-clamp-2">{t("nav.configuratorDesc", "Upload je eigen 3D-model en bestel direct.")}</p>
                    </div>
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-white border-b border-zinc-200"
          >
            <div className="px-6 py-4 flex flex-col">
              <form onSubmit={submitSearch} className="mb-3 relative" data-testid="mobile-search-form">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("nav.searchPlaceholder", "Zoek producten…")}
                  data-testid="mobile-search-input"
                  className="w-full bg-zinc-100 focus:bg-white rounded-full pl-4 pr-10 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 border border-zinc-200 focus:border-zinc-300 outline-none focus:outline-none focus:ring-0 transition-colors"
                />
                <button type="submit" aria-label="Search" className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900">
                  <Search strokeWidth={1.5} className="w-5 h-5" />
                </button>
              </form>
              <Link to="/shop" data-testid="mobile-nav-shop" className="py-2.5 text-zinc-900 font-medium border-b border-zinc-100">
                {t("nav.shop")}
              </Link>
              <span className="pt-3 pb-1 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold">{t("nav.shopByDevice")}</span>
              {menu.map((c) => (
                <Link key={c.id || c.slug} to={c.to} className="py-2 pl-3 text-sm text-zinc-600 border-b border-zinc-100 last:border-0">
                  {c.label}
                </Link>
              ))}
              <Link
                to="/configurator"
                data-testid="mobile-nav-configurator"
                className="flex items-center gap-2.5 py-2 pl-3 text-sm text-zinc-600 border-b border-zinc-100 last:border-0"
              >
                <Box strokeWidth={1.5} className="h-4 w-4 shrink-0 text-gold" />
                {t("nav.configurator")}
              </Link>
              <div className="mt-2">
                {links.map((n) => (
                  <Link key={n.key} to={n.to} className="block py-2.5 text-zinc-700 border-b border-zinc-100 last:border-0">
                    {t(`nav.${n.key}`)}
                  </Link>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-100">
                <span className="block pb-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold">{t("footer.language")}</span>
                <div className="flex gap-2" data-testid="mobile-lang-switch">
                  {LANGUAGES.map((l) => {
                    const active = (i18n.resolvedLanguage || i18n.language || "nl").slice(0, 2) === l.code;
                    return (
                      <button
                        key={l.code}
                        type="button"
                        data-testid={`mobile-lang-${l.code}`}
                        onClick={() => switchLanguage(l.code)}
                        aria-pressed={active}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "border-gold text-gold" : "border-zinc-200 text-zinc-600 hover:text-zinc-900"}`}
                      >
                        {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
